import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { 
  getMyWallet, 
  getWalletTransactions, 
  getWalletAddressBook, 
  addWalletAddress, 
  deleteWalletAddress,
  getAllWalletsSummary 
} from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Wallet, 
  Copy, 
  Check, 
  AlertTriangle, 
  Plus, 
  Trash2, 
  ExternalLink,
  Users,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Importi: separatore migliaia + 2 decimali, come nella scheda utente
const money = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Truncate address for display
const truncateAddress = (address) => {
  if (!address) return 'Non configurato';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export default function ArbicsxWallet() {
  const { isAdmin, user: authUser } = useAuth();
  const { impersonatedUser, isImpersonating } = useModule();
  
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsHasData, setTransactionsHasData] = useState(false);
  const [addressBook, setAddressBook] = useState([]);
  const [allWallets, setAllWallets] = useState(null);
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Add address modal
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: '', address: '', notes: '' });
  const [addingAddress, setAddingAddress] = useState(false);
  
  // Current user for API calls
  const currentUserId = isImpersonating ? impersonatedUser?.id : authUser?.id;
  const impersonateId = isImpersonating ? impersonatedUser?.id : undefined;
  // Stessa regola del menu (Layout.js): niente viste aggregate mentre si è dentro un profilo utente
  const canSeeAllWallets = isAdmin && !isImpersonating;

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const [walletRes, txRes, addressRes] = await Promise.all([
        getMyWallet(impersonateId),
        getWalletTransactions(currentUserId),
        getWalletAddressBook(currentUserId)
      ]);
      
      setWalletData(walletRes.data);
      setTransactions(txRes.data.transactions || []);
      setTransactionsHasData(txRes.data.has_data || false);
      setAddressBook(addressRes.data.addresses || []);
      
      // Vista "Tutti i Wallet": solo admin sul proprio profilo, mai in impersonazione
      if (canSeeAllWallets) {
        const allRes = await getAllWalletsSummary();
        setAllWallets(allRes.data);
      } else {
        setAllWallets(null);
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
      toast.error('Errore nel caricamento dati wallet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canSeeAllWallets) {
      setAllWallets(null);
    }
  }, [canSeeAllWallets]);

  useEffect(() => {
    if (currentUserId) {
      fetchWalletData();
    }
  }, [currentUserId, isAdmin, isImpersonating, impersonateId]);

  const handleCopyAddress = async () => {
    if (!walletData?.trustwallet_address) return;
    try {
      await navigator.clipboard.writeText(walletData.trustwallet_address);
      setCopied(true);
      toast.success('Indirizzo copiato!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Errore nella copia');
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.label || !newAddress.address) {
      toast.error('Compila label e indirizzo');
      return;
    }
    
    setAddingAddress(true);
    try {
      await addWalletAddress(currentUserId, newAddress);
      toast.success('Indirizzo aggiunto alla rubrica');
      setShowAddAddress(false);
      setNewAddress({ label: '', address: '', notes: '' });
      fetchWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta');
    } finally {
      setAddingAddress(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await deleteWalletAddress(currentUserId, addressId);
      toast.success('Indirizzo rimosso');
      fetchWalletData();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const copyAddressFromBook = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Indirizzo copiato!');
    } catch (err) {
      toast.error('Errore nella copia');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Il Mio Wallet</h1>
          <p className="ht-page-sub">
            {isImpersonating ? `Wallet di ${impersonatedUser?.name}` : 'Gestisci il tuo wallet crypto'}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchWalletData}
          className="ht-btn self-start sm:self-auto"
          data-testid="refresh-wallet-btn"
        >
          <RefreshCw size={14} />
          Aggiorna
        </button>
      </div>

      {/* Tabs solo per admin sul proprio profilo (nascosti in impersonazione) */}
      {canSeeAllWallets ? (
        <Tabs defaultValue="my-wallet" className="w-full">
          <TabsList className="ht-tabs bg-transparent border-0 p-0 h-auto">
            <TabsTrigger value="my-wallet" className="ht-tab">
              <Wallet size={14} />
              Il Mio Wallet
            </TabsTrigger>
            <TabsTrigger value="all-wallets" className="ht-tab" data-testid="all-wallets-tab">
              <Users size={14} />
              Tutti i Wallet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-wallet" className="mt-4">
            <WalletContent 
              walletData={walletData}
              transactions={transactions}
              transactionsHasData={transactionsHasData}
              addressBook={addressBook}
              copied={copied}
              onCopyAddress={handleCopyAddress}
              onAddAddress={() => setShowAddAddress(true)}
              onDeleteAddress={handleDeleteAddress}
              onCopyFromBook={copyAddressFromBook}
            />
          </TabsContent>

          <TabsContent value="all-wallets" className="mt-4">
            <AllWalletsView data={allWallets} />
          </TabsContent>
        </Tabs>
      ) : (
        <WalletContent 
          walletData={walletData}
          transactions={transactions}
          transactionsHasData={transactionsHasData}
          addressBook={addressBook}
          copied={copied}
          onCopyAddress={handleCopyAddress}
          onAddAddress={() => setShowAddAddress(true)}
          onDeleteAddress={handleDeleteAddress}
          onCopyFromBook={copyAddressFromBook}
        />
      )}

      {/* Add Address Modal */}
      <Dialog open={showAddAddress} onOpenChange={setShowAddAddress}>
        <DialogContent className="ht-panel text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="ht-panel-title flex items-center gap-2 text-base">
              <Plus className="ht-pos" size={18} />
              Aggiungi Indirizzo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Etichetta *</Label>
              <Input
                value={newAddress.label}
                onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                placeholder="Es: Binance, Coinbase, Amico..."
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Indirizzo Wallet *</Label>
              <Input
                value={newAddress.address}
                onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                placeholder="0x..."
                className="bg-[#11161d] border-[#282c34] text-white font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Note (opzionale)</Label>
              <Input
                value={newAddress.notes}
                onChange={(e) => setNewAddress({ ...newAddress, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="ht-btn" onClick={() => setShowAddAddress(false)}>
              Annulla
            </button>
            <button
              type="button"
              onClick={handleAddAddress}
              disabled={addingAddress}
              className="ht-btn ht-btn-primary"
              data-testid="save-address-btn"
            >
              {addingAddress ? 'Salvataggio...' : 'Salva'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Wallet Content Component
function WalletContent({ 
  walletData, 
  transactions, 
  transactionsHasData,
  addressBook, 
  copied, 
  onCopyAddress, 
  onAddAddress, 
  onDeleteAddress,
  onCopyFromBook 
}) {
  const usdt = walletData?.wallet_balance_usdt || 0;
  const usdc = walletData?.wallet_balance_usdc || 0;
  const ethUsd = walletData?.eth_balance_usd || 0;
  const ethLow = walletData?.eth_low || false;

  return (
    <div className="space-y-4">
      {/* Sintesi: totale grande a sinistra, saldi a destra */}
      <div className="ht-panel" data-testid="wallet-main-card">
        <div className="ht-hero">
          <div className="ht-hero-main">
            <span className="ht-hero-label">Totale disponibile</span>
            <p className="ht-hero-num ht-pos">{money(usdt + usdc)}</p>
            <div className="flex items-center gap-2 mt-3 min-w-0">
              <span className="ht-num text-xs text-[#8b919c] truncate" data-testid="wallet-address">
                {truncateAddress(walletData?.trustwallet_address)}
              </span>
              {walletData?.trustwallet_address && (
                <button
                  type="button"
                  onClick={onCopyAddress}
                  className="ht-btn ht-btn-icon"
                  aria-label="Copia indirizzo"
                  data-testid="copy-address-btn"
                >
                  {copied ? <Check size={13} className="ht-pos" /> : <Copy size={13} />}
                </button>
              )}
              {walletData?.trustwallet_address && (
                <a
                  href={`https://etherscan.io/address/${walletData.trustwallet_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ht-btn ht-btn-icon"
                  aria-label="Apri su Etherscan"
                >
                  <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>

          <div className="ht-hero-side ht-rows">
            <div className="ht-row">
              <span className="ht-row-label">USDT</span>
              <span className="ht-row-value ht-pos" data-testid="usdt-balance">{money(usdt)}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label">USDC</span>
              <span className="ht-row-value ht-pos" data-testid="usdc-balance">{money(usdc)}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label">ETH (gas)</span>
              <span className={`ht-row-value ${ethLow ? 'ht-alert' : ''}`} data-testid="eth-balance">
                {money(ethUsd)}
              </span>
            </div>
            {ethLow && (
              <div className="ht-row">
                <span className="ht-row-label ht-alert" data-testid="eth-low-alert">
                  <AlertTriangle size={12} />
                  ETH insufficiente per transazioni
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Storico movimenti */}
        <div className="ht-panel" data-testid="transactions-section">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#282c34]">
            <Clock size={14} className="text-[#5f6672]" />
            <h3 className="ht-panel-title text-sm">Storico Movimenti</h3>
          </div>
          
          {!transactionsHasData ? (
            <div className="ht-empty">
              Storico in arrivo
              <p className="text-xs mt-1">I movimenti verranno mostrati qui</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="ht-empty">Nessun movimento registrato</div>
          ) : (
            <div className="ht-list max-h-80 overflow-y-auto">
              {transactions.map((tx, idx) => (
                <div key={tx.id || idx} className="ht-item">
                  {tx.type === 'in' ? (
                    <ArrowDownLeft size={14} className="ht-pos flex-shrink-0" />
                  ) : (
                    <ArrowUpRight size={14} className="ht-alert flex-shrink-0" />
                  )}
                  <div className="ht-item-main">
                    <p className="ht-item-t">{tx.description || tx.type}</p>
                    <p className="ht-item-s">{formatDate(tx.timestamp)}</p>
                  </div>
                  <span className={`ht-item-n ${tx.type === 'in' ? 'ht-pos' : 'ht-alert'}`}>
                    {tx.type === 'in' ? '+' : '-'}{money(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rubrica indirizzi */}
        <div className="ht-panel" data-testid="address-book-section">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#282c34]">
            <div className="flex items-center gap-2 min-w-0">
              <Copy size={14} className="text-[#5f6672]" />
              <h3 className="ht-panel-title text-sm">Rubrica Indirizzi</h3>
            </div>
            <button
              type="button"
              onClick={onAddAddress}
              className="ht-btn ht-btn-sm ht-btn-primary"
              data-testid="add-address-btn"
            >
              <Plus size={13} />
              Aggiungi
            </button>
          </div>
          
          {addressBook.length === 0 ? (
            <div className="ht-empty">
              Nessun indirizzo salvato
              <p className="text-xs mt-1">Salva gli indirizzi che usi spesso</p>
            </div>
          ) : (
            <div className="ht-list max-h-80 overflow-y-auto">
              {addressBook.map((addr) => (
                <div key={addr.id} className="ht-item">
                  <div className="ht-item-main">
                    <p className="ht-item-t">{addr.label}</p>
                    <p className="ht-item-s ht-num">{truncateAddress(addr.address)}</p>
                    {addr.notes && <p className="ht-item-s">{addr.notes}</p>}
                  </div>
                  <div className="ht-item-actions">
                    <button
                      type="button"
                      onClick={() => onCopyFromBook(addr.address)}
                      className="ht-btn ht-btn-icon"
                      aria-label="Copia indirizzo"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteAddress(addr.id)}
                      className="ht-btn ht-btn-icon ht-btn-danger"
                      aria-label="Elimina indirizzo"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// All Wallets Admin View
function AllWalletsView({ data }) {
  if (!data) {
    return (
      <div className="ht-panel ht-empty">
        <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>Caricamento dati wallet...</p>
      </div>
    );
  }

  const { users, totals, users_with_low_eth } = data;

  return (
    <div className="space-y-4">
      {/* Striscia totali */}
      <div className="ht-totals">
        <div>
          <span className="ht-totals-label">Totale USDT</span>
          <p className="ht-totals-num ht-pos" data-testid="total-usdt">{money(totals.usdt)}</p>
        </div>
        <div>
          <span className="ht-totals-label">Totale USDC</span>
          <p className="ht-totals-num ht-pos" data-testid="total-usdc">{money(totals.usdc)}</p>
        </div>
        <div>
          <span className="ht-totals-label">Totale ETH</span>
          <p className="ht-totals-num">{money(totals.eth_usd)}</p>
        </div>
        <div>
          <span className="ht-totals-label">
            <AlertTriangle size={12} className={users_with_low_eth > 0 ? 'ht-alert' : 'ht-off'} />
            ETH basso
          </span>
          <p className={`ht-totals-num ${users_with_low_eth > 0 ? 'ht-alert' : 'ht-off'}`}>
            {users_with_low_eth || 0} utenti
          </p>
        </div>
      </div>

      {/* Una riga-card per utente */}
      <div data-testid="all-wallets-table">
        {users.map((u) => (
          <div
            key={u.id}
            className={`ht-rowcard neon n2 ${u.eth_low ? 'cr ht-state-alert' : ''}`}
            data-testid={`wallet-row-${u.id}`}
          >
            <div className="ht-rowcard-name">
              <p className="ht-item-t">{u.name}</p>
              <p className="ht-item-s ht-num">{truncateAddress(u.trustwallet_address)}</p>
            </div>
            <div className="ht-rowcard-cell">
              <span className="ht-cell-label">USDT</span>
              <p className="ht-cell-num ht-pos">{money(u.wallet_balance_usdt)}</p>
            </div>
            <div className="ht-rowcard-cell">
              <span className="ht-cell-label">USDC</span>
              <p className="ht-cell-num ht-pos">{money(u.wallet_balance_usdc)}</p>
            </div>
            <div className="ht-rowcard-cell">
              <span className="ht-cell-label">ETH</span>
              <p className={`ht-cell-num ${u.eth_low ? 'ht-alert' : ''}`}>
                {money(u.eth_balance_usd)}
              </p>
            </div>
            <div className="ml-auto">
              {u.eth_low ? (
                <span className="ht-tag ht-tag-alert">
                  <AlertTriangle size={11} />
                  ETH basso
                </span>
              ) : (
                <span className="ht-tag">OK</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
